import firebase from 'firebase/functions';
import { Collection, Inventory, Patient } from 'lib/documents';
import { Clinic } from 'lib/documents/clinic';
import { Clinician } from 'lib/documents/clinician';
import {
  AssessmentIndexEntry,
  AssessmentType,
  assessmentTypeSchema
} from 'lib/documents/patient-assessment/patient-assessment';
import { cloneDeepWith } from 'lodash';
import moment, { unix } from 'moment';
import { z } from 'zod';
import {
  httpsCallableWrapper,
  typedHttpsCallableWrapper,
  typedOnRequestWrapper
} from '../api-_utils';
import {
  deserialiseAPIObject,
  parseAPIJSON_legacy,
  parseAPISafeJSON
} from '../parseAPIJSON';
import { API_ROUTE_CONFIG } from './api-config/config';

export const htmlToPDF = (
  html: string
): Promise<firebase.HttpsCallableResult> =>
  httpsCallableWrapper(API_ROUTE_CONFIG.pdfExport.pdfExport)({ html });
/**
 * Copied from backend
 */

export interface PatientInfo extends Patient {
  __assessmentData__: {
    /**
     * Includes pending assessments
     */
    recentAssessmentDate: moment.Moment | null;
    /**
     * The last confirmed assesment
     */
    lastCompletedAssessment: moment.Moment | null;
  };
  __impactData__: {
    passportId: string | null;
    testTakerId: string | null;
  };
}

export const listPatientsWithAssessmentStatus = (options: ListUserAPIOptions) =>
  typedHttpsCallableWrapper(
    API_ROUTE_CONFIG.clinic.list.patientsWithAssessmentStatus
  )(options).then(parseAPISafeJSON);

export const getImpactTestURL = typedHttpsCallableWrapper(
  API_ROUTE_CONFIG.impact.getImPactTestURL
);

export const impactPassportIdExists = typedHttpsCallableWrapper(
  API_ROUTE_CONFIG.impact.impactPassportIdExists
);

/**
 * Start a new assessment for a test-taker
 * @param patientId The ID of the current authenticated user is used. This field is not used
 * @param locationCode
 * @returns
 */
export const initAssessment = (patientId: string, locationCode: string) =>
  typedHttpsCallableWrapper(API_ROUTE_CONFIG.assessment.initAssessment)({
    patientId,
    locationCode: locationCode as any
  })
    // Convert the output to a usable structure
    .then(res => ({
      ...res,
      data: cloneDeepWith(res.data, value => {
        if (value === null) return;
        if (value.__date__ && value.unix) return unix(value.unix);
        return;
      })
    }));

export const verifyEmail = (redirectUrl: string) =>
  httpsCallableWrapper(API_ROUTE_CONFIG.auth.user.sendEmailVerification)({
    redirectUrl
  });

export interface ListUserAPIOptions {
  clinicId: string;
  pageSize?: number;
  cursor?: string;
  pendingUsers?: boolean;
}
export type ListUserAPIOutput<T> = {
  data: T[];
  cursor: string | null;
  errors: { message: string; status: string; userId: string }[];
};
export const listPatients = (options: ListUserAPIOptions) =>
  typedHttpsCallableWrapper(API_ROUTE_CONFIG.clinic.list.patients)(
    options
  ).then(parseAPISafeJSON);

export const listClinicians = (options: ListUserAPIOptions) =>
  typedHttpsCallableWrapper(API_ROUTE_CONFIG.clinic.list.clinicians)(
    options
  ).then(parseAPISafeJSON);

export const addPatientAPIOptionsSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  clinicianId: z.string(),
  assessmentType: assessmentTypeSchema,
  clinicId: z.string()
});

export interface AddPatientAPIOptions
  extends z.infer<typeof addPatientAPIOptionsSchema> {}
export const addPatient = (data: AddPatientAPIOptions) =>
  httpsCallableWrapper(API_ROUTE_CONFIG.clinic.invite.patient)(data);

export const addClinician = (data: {
  firstName: string;
  lastName: string;
  email: string;
  clinicId: string;
}) => httpsCallableWrapper(API_ROUTE_CONFIG.clinic.invite.clinician)(data);

export const postSetPassword = () =>
  httpsCallableWrapper(API_ROUTE_CONFIG.auth.user.postSetPassword)();

export const getClinicBasicInfo = (clinicId: string) =>
  httpsCallableWrapper<{ clinicId: string }, { name: string }>(
    API_ROUTE_CONFIG.clinic.get.basicInfo
  )({ clinicId });

export const requestPasswordResetCode = () =>
  httpsCallableWrapper<any, string>(
    API_ROUTE_CONFIG.auth.user.requestPasswordResetCode
  )();

export const clinicActionAllowPatientAssessments = (patientId: string) =>
  httpsCallableWrapper<{ patientId: string }, string>(
    API_ROUTE_CONFIG.clinic.action.patient.allowAssessments
  )({
    patientId
  });

export const clinicActionDisablePatientAssessments = (patientId: string) =>
  httpsCallableWrapper<{ patientId: string }, string>(
    API_ROUTE_CONFIG.clinic.action.patient.disableAssessments
  )({
    patientId
  });

export const clinicActionResendInvitation = (args: {
  invitationId: string;
  resendType: 'sms' | 'email';
}) =>
  httpsCallableWrapper<
    { invitationId: string; resendType: 'sms' | 'email' },
    'Ok'
  >(API_ROUTE_CONFIG.clinic.action.resendInvitation)(args);

export const clinicActionEditPatientDetails = (args: {
  patientId: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string | null;
  assessmentType: AssessmentType;
  clinicianId: string;
  clinicId: string;
}) =>
  httpsCallableWrapper<
    {
      patientId: string;
      firstName: string;
      lastName: string;
      email: string;
      phoneNumber?: string | null;
      clinicianId: string;
      clinicId: string;
    },
    'Ok'
  >(API_ROUTE_CONFIG.clinic.action.patient.editDetails)(args).then(
    parseAPIJSON_legacy
  );

export const clinicActionGetInvitationLink = (args: { invitationId: string }) =>
  httpsCallableWrapper<{ invitationId: string }, string>(
    API_ROUTE_CONFIG.clinic.action.getInvitationLink
  )(args);

export const clinicActionEditClinicianDetails = (args: {
  uid: string;
  firstName: string;
  lastName: string;
  email: string;
}) =>
  typedHttpsCallableWrapper(
    API_ROUTE_CONFIG.clinic.action.clinician.editDetails
  )(args).then(parseAPIJSON_legacy);

export const invokeEnhancedTreatmentRecommendations = (patientId: string) =>
  httpsCallableWrapper(
    API_ROUTE_CONFIG.assessment.enhancedTreatmentRecommendations
  )({
    patientId
  });

export const clinicInfoEdit = typedHttpsCallableWrapper(
  API_ROUTE_CONFIG.clinic.info.edit
);

export const clinicInfoEditRequestImageUploadURL = (clinicInfo: {
  clinicId: string;
  fileType: 'png' | 'jpeg';
}) =>
  httpsCallableWrapper<any, { expires: string; url: string }>(
    API_ROUTE_CONFIG.clinic.info.editRequestImageUploadURL
  )(clinicInfo).then(parseAPIJSON_legacy);

export const clinicGetAllInfo = (clinicId: string) =>
  httpsCallableWrapper<any, { clinic: Clinic; altLink?: string }>(
    API_ROUTE_CONFIG.clinic.info.get.all
  )({ clinicId }).then(parseAPIJSON_legacy);

export const invokeCalculatePatientSubtype = (options: {
  patientId: string;
  collection:
    | Collection.PendingPatientAssessments
    | Collection.PatientAssessments;
  destId: string;
}) =>
  httpsCallableWrapper(
    API_ROUTE_CONFIG.assessment.invokeCalculatePatientSubtype
  )(options);

export const assessmentSubmitInventory = (
  inventoryName: string,
  inventoryData: Omit<Inventory, 'date' | 'id'>
) =>
  httpsCallableWrapper(API_ROUTE_CONFIG.assessment.submitInventory)({
    inventory: inventoryName,
    inventoryData
  });

export const assessmentGetPendingAssessment = (patientId: string) =>
  typedHttpsCallableWrapper(
    API_ROUTE_CONFIG.assessment.pending.get.getPendingAssessment
  )({
    patientId
  }).then(parseAPISafeJSON);

export const changeAssessmentType = (
  patientId: string,
  assessmentType: AssessmentType
) =>
  httpsCallableWrapper(API_ROUTE_CONFIG.assessment.changeAssessmentType)({
    patientId,
    assessmentType
  });

export const getClinicianData = () =>
  httpsCallableWrapper<Record<string, never>, Clinician>(
    API_ROUTE_CONFIG.clinician.getClinicianData
  )();
export const listPatientAssessmentIndex = (patientId: string) =>
  httpsCallableWrapper<{ patientId: string }, AssessmentIndexEntry[]>(
    API_ROUTE_CONFIG.patient.list.patientAssessmentIndex
  )({ patientId }).then(_ => deserialiseAPIObject(_.data));

export const processActivateAssessment = (token: string, clinicId: string) =>
  httpsCallableWrapper(
    API_ROUTE_CONFIG.clinic.payment.processActivateAssessment
  )({ token, clinicId });

export const getClinicPaymentConfig = (clinicId: string) =>
  httpsCallableWrapper<{ clinicId: string }, Clinic['paymentConfig']>(
    API_ROUTE_CONFIG.clinic.payment.getClinicPaymentConfig
  )({
    clinicId
  });

export const getPatientImpactProfile = typedHttpsCallableWrapper(
  API_ROUTE_CONFIG.patient.get.impactProfile
);

/**
 * Create a new clinic (organisation)
 */
export const apiClinicCreate = typedHttpsCallableWrapper(
  API_ROUTE_CONFIG.clinic.create
);

/**
 * Invite a clinic admin to a clinic (organisation)
 */
export const apiInviteClinicAdmin = typedHttpsCallableWrapper(
  API_ROUTE_CONFIG.clinic.invite.clinicadmin
);

export const apiClinicList = typedHttpsCallableWrapper(
  API_ROUTE_CONFIG.clinic.list.clinics,
  // This is needed to resolve an issue with type inference
  _ => parseAPISafeJSON(_)
);

export const apiClinicOnboardingRegisterUser = typedOnRequestWrapper(
  API_ROUTE_CONFIG.clinic.onboarding.registerUser
);

export const apiClinicOnboardingRegisterOrganisation =
  typedHttpsCallableWrapper(
    API_ROUTE_CONFIG.clinic.onboarding.registerOrganisation
  );
export const apiClinicGetClinicAdminData = typedHttpsCallableWrapper(
  API_ROUTE_CONFIG.clinician.get.clinicAdmin
);

export const apiClinicOnboardingGenerateToken = typedHttpsCallableWrapper(API_ROUTE_CONFIG.clinic.onboarding.generateToken);
export const apiClinicOnboardingValidateToken = typedOnRequestWrapper(API_ROUTE_CONFIG.clinic.onboarding.validateToken);